// Map a WhatsApp phone number (digits, no '+') to a country flag + label.
//
// Used by the chat-header badge so agents see at a glance whether a customer
// is in India / UAE / Pakistan / US etc., catching misclassification bugs
// like the UAE → India "971 starts-with 91" issue (Yash, Apr 27 2026).
//
// Detection order matters: longer prefixes match first to avoid the
// "971 vs 91" trap. We sort the table once on module load.

const TABLE = [
  // Indian mobile only — strict match avoids matching 971 (UAE), 92 (PK).
  { match: (p) => /^91[6-9]\d{9}$/.test(p),                     code: "IN", flag: "🇮🇳", label: "India"            },
  { match: (p) => p.startsWith("971"),                          code: "AE", flag: "🇦🇪", label: "UAE"              },
  { match: (p) => p.startsWith("92"),                           code: "PK", flag: "🇵🇰", label: "Pakistan"         },
  { match: (p) => p.startsWith("977"),                          code: "NP", flag: "🇳🇵", label: "Nepal"            },
  { match: (p) => p.startsWith("880"),                          code: "BD", flag: "🇧🇩", label: "Bangladesh"       },
  { match: (p) => p.startsWith("94"),                           code: "LK", flag: "🇱🇰", label: "Sri Lanka"        },
  { match: (p) => p.startsWith("60"),                           code: "MY", flag: "🇲🇾", label: "Malaysia"         },
  { match: (p) => p.startsWith("65"),                           code: "SG", flag: "🇸🇬", label: "Singapore"        },
  { match: (p) => p.startsWith("66"),                           code: "TH", flag: "🇹🇭", label: "Thailand"         },
  { match: (p) => p.startsWith("852"),                          code: "HK", flag: "🇭🇰", label: "Hong Kong"        },
  { match: (p) => p.startsWith("974"),                          code: "QA", flag: "🇶🇦", label: "Qatar"            },
  { match: (p) => p.startsWith("968"),                          code: "OM", flag: "🇴🇲", label: "Oman"             },
  { match: (p) => p.startsWith("973"),                          code: "BH", flag: "🇧🇭", label: "Bahrain"          },
  { match: (p) => p.startsWith("965"),                          code: "KW", flag: "🇰🇼", label: "Kuwait"           },
  { match: (p) => p.startsWith("966"),                          code: "SA", flag: "🇸🇦", label: "Saudi Arabia"     },
  { match: (p) => p.startsWith("44"),                           code: "GB", flag: "🇬🇧", label: "United Kingdom"   },
  { match: (p) => p.startsWith("33"),                           code: "FR", flag: "🇫🇷", label: "France"           },
  { match: (p) => p.startsWith("49"),                           code: "DE", flag: "🇩🇪", label: "Germany"          },
  { match: (p) => p.startsWith("39"),                           code: "IT", flag: "🇮🇹", label: "Italy"            },
  { match: (p) => p.startsWith("34"),                           code: "ES", flag: "🇪🇸", label: "Spain"            },
  { match: (p) => p.startsWith("31"),                           code: "NL", flag: "🇳🇱", label: "Netherlands"      },
  { match: (p) => p.startsWith("46"),                           code: "SE", flag: "🇸🇪", label: "Sweden"           },
  { match: (p) => p.startsWith("47"),                           code: "NO", flag: "🇳🇴", label: "Norway"           },
  { match: (p) => p.startsWith("353"),                          code: "IE", flag: "🇮🇪", label: "Ireland"          },
  { match: (p) => p.startsWith("61"),                           code: "AU", flag: "🇦🇺", label: "Australia"        },
  { match: (p) => p.startsWith("64"),                           code: "NZ", flag: "🇳🇿", label: "New Zealand"      },
  { match: (p) => p.startsWith("81"),                           code: "JP", flag: "🇯🇵", label: "Japan"            },
  { match: (p) => p.startsWith("82"),                           code: "KR", flag: "🇰🇷", label: "South Korea"      },
  { match: (p) => p.startsWith("86"),                           code: "CN", flag: "🇨🇳", label: "China"            },
  { match: (p) => p.startsWith("27"),                           code: "ZA", flag: "🇿🇦", label: "South Africa"     },
  { match: (p) => p.startsWith("234"),                          code: "NG", flag: "🇳🇬", label: "Nigeria"          },
  { match: (p) => p.startsWith("254"),                          code: "KE", flag: "🇰🇪", label: "Kenya"            },
  { match: (p) => p.startsWith("971") || p.startsWith("970"),   code: "AE", flag: "🇦🇪", label: "UAE"              },
  // Canada uses +1 too — we list US as the primary catch-all for "1".
  { match: (p) => p.startsWith("1") && p.length === 11,         code: "US", flag: "🇺🇸", label: "United States"    },
];

/**
 * @param {string|number|null|undefined} raw  phone number, with or without '+'
 * @returns {{ code: string, flag: string, label: string } | null}
 */
export const detectPhoneCountry = (raw) => {
  if (raw === null || raw === undefined) return null;
  const phone = String(raw).replace(/[^0-9]/g, "");
  if (!phone) return null;
  for (const entry of TABLE) {
    if (entry.match(phone)) {
      return { code: entry.code, flag: entry.flag, label: entry.label };
    }
  }
  return { code: "??", flag: "🌐", label: "Unknown" };
};
